<script setup>
// NOTICE: KAPTION-INTEGRATION — Aggregate Campaign Reports dashboard.
// Composes the summary metric cards, delivery funnel, time-series chart,
// conversion-rate bars and the top-campaigns leaderboard. All data is
// PRE-AGGREGATED by the wanalytics API (Kaption::AnalyticsDataService) and
// passed in as props — this page does no aggregation, only layout + filter UI.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import KaptionMetricCard from 'dashboard/components-next/Campaigns/KaptionMetricCard.vue';
import KaptionMetricFunnel from 'dashboard/components-next/Campaigns/KaptionMetricFunnel.vue';
import KaptionDeliveryChart from 'dashboard/components-next/Campaigns/KaptionDeliveryChart.vue';
import KaptionConversionRates from 'dashboard/components-next/Campaigns/KaptionConversionRates.vue';
import KaptionTopCampaignsTable from 'dashboard/components-next/Campaigns/KaptionTopCampaignsTable.vue';
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';
import KaptionAggregateExportModal from 'dashboard/components-next/Campaigns/KaptionAggregateExportModal.vue';

const props = defineProps({
  totals: {
    type: Object,
    default: () => ({}),
  },
  timeline: {
    type: Array,
    default: () => [],
  },
  campaigns: {
    type: Array,
    default: () => [],
  },
  // WhatsApp inboxes for the per-inbox filter — array of { id, name, phone_number }
  inboxes: {
    type: Array,
    default: () => [],
  },
  // Campaigns for the individual-campaign multi-select filter —
  // array of { id, title, campaign_type }
  filterCampaigns: {
    type: Array,
    default: () => [],
  },
  // Loading state (page wrapper sets this while fetching from the API).
  loading: {
    type: Boolean,
    default: false,
  },
  // Initial date-range selection (days as string): '7' | '30' | '90'.
  initialDateRange: {
    type: String,
    default: '7',
  },
  // Optional async resolver `(campaignId) => template|null` for lazy-loading the
  // template preview. Null (default) makes the leaderboard fall back to an inline
  // `campaign.template` (story/tests); the page supplies a lazy-fetching one.
  resolveTemplate: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['selectCampaign', 'filterChange']);

const { t } = useI18n();

// --- Filter UI state ---
const TYPE_OPTIONS = [
  { value: 'all', labelKey: 'CAMPAIGN.REPORT.DASHBOARD.FILTER_TYPE_ALL' },
  {
    value: 'campaigns',
    labelKey: 'CAMPAIGN.REPORT.DASHBOARD.FILTER_TYPE_CAMPAIGNS',
  },
  { value: 'direct', labelKey: 'CAMPAIGN.REPORT.DASHBOARD.FILTER_TYPE_DIRECT' },
];
const GRANULARITY_OPTIONS = [
  { value: 'day', labelKey: 'CAMPAIGN.REPORT.DASHBOARD.GRANULARITY_DAY' },
  { value: 'week', labelKey: 'CAMPAIGN.REPORT.DASHBOARD.GRANULARITY_WEEK' },
  { value: 'month', labelKey: 'CAMPAIGN.REPORT.DASHBOARD.GRANULARITY_MONTH' },
];

const typeFilter = ref('all');
const granularity = ref('day');
const selectedInboxId = ref('all');
const selectedCampaignIds = ref([]);
const dateRange = ref(props.initialDateRange);
// Include archived campaigns in the aggregates (default ON). When OFF the page
// asks the backend to exclude archived campaigns from the wanalytics query.
// kaption-campaign-reports.DASHBOARD.3
const includeArchived = ref(true);

// Date-range presets (single-select). Value is the number of days back; 'all'
// means all-time (the page omits the date window so the worker aggregates the
// full history).
// kaption-campaign-reports.DASHBOARD.1
const dateRangeOptions = computed(() => [
  { value: '7', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_7_DAYS') },
  { value: '14', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_14_DAYS') },
  { value: '30', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_30_DAYS') },
  { value: '90', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_90_DAYS') },
  { value: '180', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_180_DAYS') },
  { value: '365', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_365_DAYS') },
  { value: 'all', label: t('CAMPAIGN.REPORT.DASHBOARD.RANGE_ALL_TIME') },
]);

// Inbox single-select options: "All Inboxes" + each inbox (name · phone).
const inboxOptions = computed(() => [
  { value: 'all', label: t('CAMPAIGN.REPORT.DASHBOARD.FILTER_ALL_INBOXES') },
  ...props.inboxes.map(inbox => ({
    value: inbox.id,
    label: inbox.phone_number
      ? `${inbox.name} (${inbox.phone_number})`
      : inbox.name,
  })),
]);

// Campaign multi-select options carry the type so the control can render a badge.
const campaignFilterOptions = computed(() =>
  props.filterCampaigns.map(campaign => ({
    value: campaign.id,
    label: campaign.title,
    type: campaign.campaign_type === 'direct' ? 'direct' : 'campaign',
  }))
);

// Emit a consolidated filter payload whenever any control changes.
const emitFilterChange = () => {
  emit('filterChange', {
    type: typeFilter.value,
    granularity: granularity.value,
    inboxId: selectedInboxId.value,
    campaignIds: selectedCampaignIds.value,
    days: dateRange.value,
    includeArchived: includeArchived.value,
  });
};

const toggleIncludeArchived = () => {
  includeArchived.value = !includeArchived.value;
  emitFilterChange();
};

// --- Summary cards ---
const summaryCards = computed(() => {
  const tot = props.totals || {};
  return [
    {
      label: t('CAMPAIGN.REPORT.METRICS.SENT'),
      value: tot.sent ?? 0,
      percentage: null,
      variant: 'slate',
      icon: 'i-lucide-send',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.DELIVERED'),
      value: tot.delivered ?? 0,
      percentage: tot.delivery_rate ?? null,
      variant: 'teal',
      icon: 'i-lucide-check-check',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.READ'),
      value: tot.read ?? 0,
      percentage: tot.read_rate ?? null,
      variant: 'teal',
      icon: 'i-lucide-eye',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.REPLIED'),
      value: tot.replied ?? 0,
      percentage: tot.reply_rate ?? null,
      variant: 'green',
      icon: 'i-lucide-reply',
    },
    // NOTICE: KAPTION-INTEGRATION — clicks on tracked kapt.io links
    {
      label: t('CAMPAIGN.REPORT.METRICS.CLICKS'),
      value: tot.clicked ?? 0,
      percentage: tot.click_rate ?? null,
      variant: 'green',
      icon: 'i-lucide-mouse-pointer-click',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.FAILED'),
      value: tot.failed ?? 0,
      percentage: tot.failure_rate ?? null,
      variant: 'ruby',
      icon: 'i-lucide-alert-triangle',
    },
  ];
});

// --- Funnel stages (Sent → Delivered → Read → Replied) ---
const funnelStages = computed(() => {
  const tot = props.totals || {};
  return [
    {
      key: 'sent',
      label: t('CAMPAIGN.REPORT.METRICS.SENT'),
      value: tot.sent ?? 0,
      percentage: null,
    },
    {
      key: 'delivered',
      label: t('CAMPAIGN.REPORT.METRICS.DELIVERED'),
      value: tot.delivered ?? 0,
      percentage: tot.delivery_rate ?? null,
    },
    {
      key: 'read',
      label: t('CAMPAIGN.REPORT.METRICS.READ'),
      value: tot.read ?? 0,
      percentage: tot.read_rate ?? null,
    },
    // NOTICE: KAPTION-INTEGRATION — clicked joins the funnel when present
    ...((tot.clicked ?? 0) > 0
      ? [
          {
            key: 'clicked',
            label: t('CAMPAIGN.REPORT.METRICS.CLICKS'),
            value: tot.clicked ?? 0,
            percentage: tot.click_rate ?? null,
          },
        ]
      : []),
    {
      key: 'replied',
      label: t('CAMPAIGN.REPORT.METRICS.REPLIED'),
      value: tot.replied ?? 0,
      percentage: tot.reply_rate ?? null,
    },
  ];
});

const funnelFailed = computed(() => ({
  value: props.totals?.failed ?? 0,
  percentage: props.totals?.failure_rate ?? null,
}));

const rates = computed(() => {
  const tot = props.totals || {};
  return {
    delivery_rate: tot.delivery_rate ?? 0,
    read_rate: tot.read_rate ?? 0,
    reply_rate: tot.reply_rate ?? 0,
    failure_rate: tot.failure_rate ?? 0,
    // NOTICE: KAPTION-INTEGRATION — CTR (clicks ÷ delivered)
    click_through_rate: tot.click_through_rate ?? 0,
  };
});

// Clicking a leaderboard row toggles that campaign in the active campaign filter,
// re-scoping every metric to the selection (reuses the campaign_ids plumbing and
// stays in sync with the header multi-select chips).
const toggleCampaignFilter = id => {
  const exists = selectedCampaignIds.value.some(x => Number(x) === Number(id));
  selectedCampaignIds.value = exists
    ? selectedCampaignIds.value.filter(x => Number(x) !== Number(id))
    : [...selectedCampaignIds.value, id];
  emitFilterChange();
};

// The ↗ action opens the full single-campaign report.
const onSelectCampaign = id => emit('selectCampaign', id);

// --- Aggregate export modal ---
// kaption-campaign-reports — exposes the CURRENT filter state to the modal so it
// can convert it into the backend `filters` payload.
const isExportModalOpen = ref(false);
const exportDashboardFilters = computed(() => ({
  type: typeFilter.value,
  granularity: granularity.value,
  inboxId: selectedInboxId.value,
  campaignIds: selectedCampaignIds.value,
  days: dateRange.value,
  includeArchived: includeArchived.value,
}));
const openExportModal = () => {
  isExportModalOpen.value = true;
};
</script>

<!-- kaption-campaign-reports.DASHBOARD.5 — renders funnel, time series,
     conversion rates and the top-campaigns leaderboard from the API props. -->
<template>
  <div class="flex flex-col gap-6 p-6 bg-n-background min-h-full">
    <!-- Header + filters -->
    <header class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-bold text-n-slate-12">
            {{ t('CAMPAIGN.REPORT.DASHBOARD.TITLE') }}
          </h1>
          <span
            v-if="loading"
            class="i-lucide-loader-circle w-4 h-4 text-n-slate-10 animate-spin"
          />
        </div>
        <p class="text-sm text-n-slate-10">
          {{ t('CAMPAIGN.REPORT.DASHBOARD.SUBTITLE') }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <!-- Type toggle -->
        <div
          class="flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-black2"
        >
          <button
            v-for="opt in TYPE_OPTIONS"
            :key="opt.value"
            type="button"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="
              typeFilter === opt.value
                ? 'bg-n-slate-12 text-n-solid-2'
                : 'text-n-slate-11 hover:bg-n-alpha-black4'
            "
            @click="
              typeFilter = opt.value;
              emitFilterChange();
            "
          >
            {{ t(opt.labelKey) }}
          </button>
        </div>

        <!-- Per-inbox filter (single-select, searchable) -->
        <KaptionFilterSelect
          v-model="selectedInboxId"
          :options="inboxOptions"
          icon="i-lucide-inbox"
          :placeholder="t('CAMPAIGN.REPORT.DASHBOARD.FILTER_ALL_INBOXES')"
          :search-placeholder="
            t('CAMPAIGN.REPORT.DASHBOARD.FILTER_INBOX_SEARCH')
          "
          @update:model-value="emitFilterChange"
        />

        <!-- Individual-campaign filter (multi-select, searchable, with type badges) -->
        <KaptionFilterSelect
          v-model="selectedCampaignIds"
          multiple
          show-badge
          :options="campaignFilterOptions"
          icon="i-lucide-megaphone"
          :placeholder="t('CAMPAIGN.REPORT.DASHBOARD.FILTER_ALL_CAMPAIGNS')"
          :search-placeholder="
            t('CAMPAIGN.REPORT.DASHBOARD.FILTER_SEARCH_CAMPAIGNS')
          "
          @update:model-value="emitFilterChange"
        />

        <!-- Date range (single-select preset) — same unified dropdown -->
        <KaptionFilterSelect
          v-model="dateRange"
          :options="dateRangeOptions"
          icon="i-lucide-calendar"
          @update:model-value="emitFilterChange"
        />

        <!-- Include-archived toggle (default ON) -->
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak transition-colors"
          :class="
            includeArchived
              ? 'bg-n-slate-12 text-n-solid-2'
              : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-black4'
          "
          :aria-pressed="includeArchived"
          @click="toggleIncludeArchived"
        >
          <span
            class="w-3.5 h-3.5"
            :class="
              includeArchived ? 'i-lucide-check-square' : 'i-lucide-square'
            "
          />
          {{ t('CAMPAIGN.REPORT.DASHBOARD.INCLUDE_ARCHIVED') }}
        </button>

        <!-- Granularity toggle -->
        <div
          class="flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-black2 ml-auto"
        >
          <button
            v-for="opt in GRANULARITY_OPTIONS"
            :key="opt.value"
            type="button"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="
              granularity === opt.value
                ? 'bg-n-slate-12 text-n-solid-2'
                : 'text-n-slate-11 hover:bg-n-alpha-black4'
            "
            @click="
              granularity = opt.value;
              emitFilterChange();
            "
          >
            {{ t(opt.labelKey) }}
          </button>
        </div>

        <!-- Export button -->
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-black4 transition-colors"
          @click="openExportModal"
        >
          <span class="i-lucide-download w-3.5 h-3.5" />
          {{ t('CAMPAIGN.REPORT.EXPORT.BUTTON') }}
        </button>
      </div>
    </header>

    <KaptionAggregateExportModal
      v-model:open="isExportModalOpen"
      :dashboard-filters="exportDashboardFilters"
    />

    <!-- Summary metric cards -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide">
        {{ t('CAMPAIGN.REPORT.DASHBOARD.SUMMARY') }}
      </h2>
      <div
        class="grid gap-3 grid-cols-2 sm:grid-cols-3"
        :class="summaryCards.length > 5 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'"
      >
        <KaptionMetricCard
          v-for="card in summaryCards"
          :key="card.label"
          :label="card.label"
          :value="card.value"
          :percentage="card.percentage"
          :variant="card.variant"
          :icon="card.icon"
        />
      </div>
    </section>

    <!-- Delivery funnel -->
    <section
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-black2 p-4"
    >
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.DASHBOARD.DELIVERY_FUNNEL') }}
      </h2>
      <KaptionMetricFunnel :stages="funnelStages" :failed="funnelFailed" />
    </section>

    <!-- Delivery time-series chart -->
    <section>
      <KaptionDeliveryChart :data="timeline" :granularity="granularity" />
    </section>

    <!-- Two-column: conversion rates + top campaigns -->
    <section class="grid gap-6 grid-cols-1 lg:grid-cols-3">
      <div class="lg:col-span-1">
        <KaptionConversionRates :rates="rates" />
      </div>
      <div class="lg:col-span-2">
        <KaptionTopCampaignsTable
          :campaigns="campaigns"
          :selected-ids="selectedCampaignIds"
          :resolve-template="resolveTemplate"
          @toggle-filter="toggleCampaignFilter"
          @open="onSelectCampaign"
        />
      </div>
    </section>
  </div>
</template>
